import {
  isTwoWaySmsKey,
  messageKeyHasLink,
  WHATSAPP_REPLY_KEYS,
  type RenderSmsOptions,
  type SmsKey,
  type SmsVars,
  type ReplySmsKey,
  type TwoWaySmsKey,
} from './copy/sms.ts';
import type { CountryPack } from './countries.ts';
import type { ConsentType, MessageChannel } from './enums.ts';
import type { E164 } from './format.ts';

/**
 * §4.5 Channel layer: every field interaction goes through these interfaces, so a channel is
 * added without touching the pipeline. Outbound messages go through the Notifier on each person's
 * channel order, which comes from the org's country pack (spec §4.6) and the person's preference.
 * Types and pure helpers only: the Notifier is `createNotifier` (`notify/notifier.ts`) and the SMS
 * drivers (Twilio, İletimerkezi, console) are `sms/transports.ts` (W1-08); the WhatsApp (Twilio)
 * and web push transports come with week 2 (M6), the calls with M8.
 *
 * Nothing here reads env: the provider, the sender id, two-way SMS, links in SMS and the voice
 * number are the pack's (GAP D47, D48); credentials stay global env in the transports.
 */

/**
 * How a report reached TalkOlog (§4.5): `web`, `voice` or `sms`. It mirrors the Postgres enum
 * `field_channel` (§6.2, `enums.ts`), which also has `office` for reports typed in the panel (D13).
 */
export type { FieldChannel } from './enums.ts';

/**
 * How TalkOlog reaches a person (§4.5, enum `message_channel`): `sms`, `whatsapp`, `push`. Push can
 * never be inbound, so it is a separate type from `FieldChannel`.
 */
export type { MessageChannel } from './enums.ts';

/** A message's copy key. WhatsApp templates and push texts reuse the SMS keys (week 2). */
export type MessageKey = SmsKey;

/**
 * The keys the Notifier sends: every key except the two-way replies (`help`, `stop_confirm`,
 * `unknown_number`, `pref_call_ok`, `pref_text_ok`), which sms-webhook sends in answer to an
 * inbound text (`smsReplyAllowed`).
 */
export type NotifierKey = Exclude<MessageKey, TwoWaySmsKey>;

/**
 * The message channels with a transport. Week 1 builds SMS; W2-14 web push (the worker registers
 * its transport, `pushChannelSpec`); W2-15 WhatsApp through Twilio (`WHATSAPP_CHANNEL`, registered
 * by the worker and the edge functions, `whatsapp/transport.ts`). A Notifier without a channel's
 * registration (the edge functions' for push) never sends on it.
 */
export const IMPLEMENTED_MESSAGE_CHANNELS = [
  'sms',
  'push',
  'whatsapp',
] as const satisfies readonly MessageChannel[];

/**
 * The consent each message channel needs (`consents.type`); null = no consent label, never
 * deliverable. Push: the `push` consent the Settings toggle gives (spec §19 Q27, W2-14). WhatsApp:
 * the `whatsapp` consent of *Message me on WhatsApp* on Field-02 or in Settings (spec §19 Q25,
 * W2-15); revoked by unticking it or by STOP on WhatsApp (§19 Q40, the first option).
 */
export const CONSENT_FOR_CHANNEL: Readonly<Record<MessageChannel, ConsentType | null>> = {
  sms: 'sms',
  whatsapp: 'whatsapp',
  push: 'push',
};

/**
 * The keys web push carries (spec §19 Q27, decided 2026-10-15): `question_ready`, `draft_ready`,
 * `filed` and the reminder ladder's (the daily link, the weather heads-up, R1, R2; `REMIND_STAGE_KEY`
 * in `schedule/remind.ts`). Never `welcome` or `verify_code`: push needs the installed app, which
 * the person reaches only through the welcome's link and the device check.
 */
export const PUSH_KEYS = [
  'question_ready',
  'draft_ready',
  'filed',
  'daily_link',
  'weather_headsup',
  'reminder_1',
  'reminder_2',
] as const satisfies readonly NotifierKey[];

/**
 * The keys WhatsApp carries (W2-15, spec §19 Q25; §19 Q372, 2026-10-20): every Notifier key. The
 * welcome and the device code joined when the owner chose WhatsApp before SMS until the SMS
 * senders are registered (Turkish carriers drop link-bearing texts from the US long code, US SMS
 * waits for 10DLC): the welcome by a WhatsApp template when the pack orders WhatsApp first (Q25's
 * alternative), the device code by an authentication template, so a WhatsApp-only worker can
 * verify a new phone (F4 step 2). Each is a business-initiated message: outside the 24-hour window
 * it needs an approved template (`whatsapp/templates.ts`); without one in the reporter's language
 * the channel is not reachable for the key and the next channel of the pack's order carries it,
 * if any (§19 Q22, `ChannelRegistration`; since §19 Q374 no pack lists SMS, so none).
 */
export const WHATSAPP_KEYS = [
  'welcome',
  'verify_code',
  'daily_link',
  'call_coming',
  'weather_headsup',
  'reminder_1',
  'reminder_2',
  'missed_call',
  'question_ready',
  'draft_ready',
  'filed',
  'photos_link',
  'edit_applied',
  'finish_report',
  'site_paused',
] as const satisfies readonly NotifierKey[];
export type WhatsappKey = (typeof WHATSAPP_KEYS)[number];

/** The keys a channel may carry when it carries only some; absent = every Notifier key. */
export const CHANNEL_KEYS: Readonly<Partial<Record<MessageChannel, readonly NotifierKey[]>>> = {
  push: PUSH_KEYS,
  whatsapp: WHATSAPP_KEYS,
};

/** Whether `channel` may carry `key` (`CHANNEL_KEYS`). */
export function channelCarriesKey(channel: MessageChannel, key: NotifierKey): boolean {
  const keys = CHANNEL_KEYS[channel];
  return keys === undefined || keys.includes(key);
}

/**
 * The channels a first-contact key may go on before, or without, their consent (§19 Q25, Q372):
 * SMS and WhatsApp, the two that reach a phone number. Never push, which needs the installed app
 * (and carries neither key, `PUSH_KEYS`).
 */
export const PRE_CONSENT_CHANNELS = [
  'sms',
  'whatsapp',
] as const satisfies readonly MessageChannel[];

/**
 * Keys the spec sends before the person has consented: the welcome at invite (F3 step 1, before
 * the consent screen of F4) and the device code (F4 step 2, before step 3's consent). On SMS and
 * WhatsApp (`PRE_CONSENT_CHANNELS`, §19 Q372) they skip the channel's consent only while the person
 * has no consent of any type, given or revoked: the office's attestation at invite
 * (`INVITE_MESSAGES_ATTESTATION`) is the opt-in for that first message. Once they have answered a
 * consent prompt, a missing consent of the channel (declined, or revoked in the field app's
 * Settings, §9.3) blocks the channel, so the office's "Re-send welcome" (§11 Team) does not reach
 * them there. The opt-out always applies. Every other key needs the channel's consent.
 */
export const PRE_CONSENT_KEYS = [
  'welcome',
  'verify_code',
] as const satisfies readonly NotifierKey[];

/**
 * Keys that need no consent at all on SMS and WhatsApp, even after the person declined or revoked
 * it: the device code, which the person asks for on the recorder to prove the number (spec §19
 * Q25; W2-02: revoking texts in Settings blocks every SMS but this one, so a reporter who turned
 * texts off can still sign in on a new phone; §19 Q372: the same on WhatsApp). Only an opt-out of
 * the channel stops it: SMS STOP (`optedOut`, the provider's list), and on WhatsApp a revoked
 * `whatsapp` consent, which is what WhatsApp's STOP records (§19 Q40; the Settings untick reads
 * the same, so it stops the code there too). It follows the person's order like any key, kept to
 * `PRE_CONSENT_CHANNELS`, and never steps outside the pack's list (§19 Q374, 2026-10-23: SMS is
 * passive in every pack, so the code goes by WhatsApp only; until then SMS was appended to the
 * order of a pack that listed none). Where WhatsApp cannot carry it, nothing is deliverable, as
 * for any key.
 */
export const CONSENT_FREE_KEYS = ['verify_code'] as const satisfies readonly NotifierKey[];

/**
 * The message channels Settings offers as a preference (F15, spec §19 Q21; W2-02): the pack's, in
 * its order, that are built, except push, which has its own *Notifications* toggle (W2-14, §19
 * Q107: push is reached by turning it on, never as a preference). The choice is shown only when
 * there is more than one (a pack that lists SMS and WhatsApp, W2-15).
 */
export function settingsMessageChannels(
  pack: Pick<CountryPack, 'messageChannels'>,
  implemented: readonly MessageChannel[] = IMPLEMENTED_MESSAGE_CHANNELS,
): MessageChannel[] {
  return pack.messageChannels.filter((c) => c !== 'push' && implemented.includes(c));
}

/**
 * A built message channel (§4.5; W2-01): what the Notifier needs to know about it besides its
 * transport. The registry is the Notifier's (`NotifierDeps.channels`): SMS is always registered
 * (`SMS_CHANNEL`); W2-14 (push) and W2-15 (WhatsApp) register theirs, tests a fake one.
 */
export interface MessageChannelSpec {
  readonly channel: MessageChannel;
  /** The consent the channel needs (`consents.type`). */
  readonly consent: ConsentType;
  /**
   * The pack's sender of this channel in this environment; null = none set here, so the channel
   * goes through `console` (§4.6, §19 Q29; WhatsApp Q39).
   */
  sender(pack: CountryPack): string | null;
  /** The pack's provider driver for this channel (`sms_provider`, `whatsapp_provider`, …). */
  provider(pack: CountryPack): string;
}

/** SMS: consent `sms`, the pack's `sms_sender_id` and `sms_provider`. */
export const SMS_CHANNEL: MessageChannelSpec = {
  channel: 'sms',
  consent: 'sms',
  sender: (pack) => pack.smsSenderId,
  provider: (pack) => pack.smsProvider,
};

/** The driver name of the web push transport (`messages.provider`, spec §6.3). */
export const PUSH_DRIVER = 'webpush';

/**
 * Web push (W2-14; spec §4.4, §19 Q27): consent `push`, no pack sender. Its "sender" is the VAPID
 * key pair of this environment (env, not the pack: standard Web Push has no provider account), so
 * without one the channel goes through `console` (`pushChannelSpec(false)`), like a pack with no
 * SMS sender. The worker registers it (`apps/worker/src/push.ts`).
 */
export function pushChannelSpec(vapidConfigured: boolean): MessageChannelSpec {
  return {
    channel: 'push',
    consent: 'push',
    sender: () => (vapidConfigured ? 'vapid' : null),
    provider: () => PUSH_DRIVER,
  };
}

/** The driver of the WhatsApp transport: the pack's `whatsapp_provider` (only `twilio`). */
export const WHATSAPP_DRIVERS = ['twilio', 'console'] as const;
export type WhatsappDriver = (typeof WHATSAPP_DRIVERS)[number];

/**
 * WhatsApp through Twilio (W2-15; spec §4.4, §19 Q25, Q39): consent `whatsapp`, the pack's
 * `whatsapp_sender_id` (per environment; null = `console`) and `whatsapp_provider`.
 */
export const WHATSAPP_CHANNEL: MessageChannelSpec = {
  channel: 'whatsapp',
  consent: 'whatsapp',
  sender: (pack) => pack.whatsappSenderId,
  provider: (pack) => pack.whatsappProvider,
};

/** The channels built in this repo, as specs (`pickMessageTransport`'s default registry). */
export const BUILT_CHANNEL_SPECS: readonly MessageChannelSpec[] = [SMS_CHANNEL, WHATSAPP_CHANNEL];

/** What a channel's transport is asked to send: the §9.2 text and what it was rendered from. */
export interface OutboundMessage {
  /** The person's number (push ignores it: its transport reads the reporter's subscriptions). */
  to: E164;
  /** The reporter the message is for (push finds their subscriptions by it); null for a reply. */
  reporterId: string | null;
  /** The text in the person's language with the pack's variant. */
  body: string;
  key: MessageKey;
  /** The language the body is in (`en`, `es`, `tr`): a WhatsApp template's language (W2-15). */
  language: string;
  /** The key's variables, unmasked (a WhatsApp template's variables, W2-15). */
  vars: Readonly<Record<string, unknown>>;
  /** The org's country pack (WhatsApp reads its template ids, W2-15). */
  pack?: CountryPack;
  /**
   * A reply to a message the person just sent (`SmsNotifier.reply`): WhatsApp sends it free-form
   * inside the 24-hour window that message opened.
   */
  reply?: boolean;
}

/**
 * One driver of one message channel (§4.5; W2-01). SMS wraps its `SmsTransport`s; W2-14 / W2-15
 * implement this directly. A refusal throws `MessageSendError` (`SmsSendError` for SMS).
 */
export interface MessageTransport {
  readonly channel: MessageChannel;
  readonly driver: string;
  /**
   * `payload`: what the row's `messages.payload` should also record about this send (WhatsApp:
   * `whatsapp_mode` template or session, and the template's content sid, W2-15).
   */
  send(
    message: OutboundMessage,
    opts: SmsSendOptions,
  ): Promise<{ providerId: string; payload?: Record<string, string> }>;
}

/**
 * A send a channel's provider refused, or a network failure (§4.5; W2-01). `retryable`: the same
 * channel may pass later (HTTP 429 or 5xx, network), so the job retries it with backoff;
 * `undeliverable`: the channel cannot carry this message to this person, final for the channel,
 * so the Notifier falls back to the next channel of the order (§19 Q22). `SmsSendError` is the
 * SMS drivers' subclass.
 */
export class MessageSendError extends Error {
  constructor(
    message: string,
    readonly channel: MessageChannel,
    readonly opts: {
      driver: string;
      /** Try again later (rate limit, provider outage, network). */
      retryable: boolean;
      /** The channel cannot reach this person with this message (invalid, landline, refused). */
      undeliverable?: boolean;
      /** The provider's own opt-out list has the person (Twilio 21610 for SMS). */
      optedOut?: boolean;
      /** The provider's error code, e.g. Twilio 21211. */
      code?: string | null;
    },
  ) {
    super(message);
    this.name = 'MessageSendError';
  }
  get retryable(): boolean {
    return this.opts.retryable;
  }
  get undeliverable(): boolean {
    return this.opts.undeliverable === true;
  }
  get optedOut(): boolean {
    return this.opts.optedOut === true;
  }
}

/** Why TalkOlog places an outbound call (§4.5, `calls.purpose`). */
export type CallPurpose = 'daily_report' | 'reminder' | 'callback';

/**
 * The site-day an outbound call is about. Week 2 (M8, outbound calls) owns the rest of this
 * shape; the spec names the type only.
 */
export interface CallContext {
  siteId: string;
  /** `YYYY-MM-DD` in the site's time zone. */
  date: string;
}

/**
 * Why a skip happened beyond its reason, for callers that answer differently: `disabled` (the
 * office disabled the reporter), `console_real_org` (a real org's pack has no sender id in this
 * environment, so the text would reach no phone), `phone_opted_out` (the phone sent STOP on the
 * pack, `sms_opt_outs`, though this reporter row is not opted out).
 */
export type NotifySkipDetail = 'disabled' | 'console_real_org' | 'phone_opted_out';

/** What `Notifier.send` did: the channel it used, or why nothing went out (audited). */
export type NotifyOutcome =
  | { channel: MessageChannel; messageId: string }
  | {
      channel: null;
      reason: 'no_channel' | 'no_consent' | 'opted_out';
      detail?: NotifySkipDetail;
    };

export interface Notifier {
  /**
   * Sends on the first channel of `deliverableChannels()` (the person's order from the pack),
   * in the reporter's language with the pack's variant (`smsRenderOptions(pack)`), through
   * `pickMessageTransport()`; writes `messages` (with `channel`) and the audit row. `channels`
   * narrows that order and never widens it (e.g. F16's "Send me today's link" by SMS);
   * `dedupeKey` is one per logical message (e.g. `notify:filed:{report_id}`). A channel that fails
   * for good (at send, or later by a status callback) hands the message to the next channel of the
   * order under the same key, one `messages` row per attempt (§19 Q22, W2-01). The two-way replies
   * are not Notifier keys (`smsReplyAllowed`).
   */
  send<K extends NotifierKey>(
    reporterId: string,
    key: K,
    vars: SmsVars<K>,
    opts?: {
      channels?: readonly MessageChannel[];
      dedupeKey?: string;
      /** The report the message is about (`messages.payload.report_id`, its cost, W2-15). */
      reportId?: string;
    },
  ): Promise<NotifyOutcome>;
  /** Places an outbound call if the reporter prefers calls, has auto-call consent and is inside the call window. */
  call(reporterId: string, purpose: CallPurpose, ctx: CallContext): Promise<void>;
}

/**
 * SMS drivers: a pack's `sms_provider` (`twilio` by default, `iletimerkezi` for the TR pack), or
 * `console` for demo orgs and packs with no sender id in this environment (`pickMessageTransport`).
 */
export const SMS_DRIVERS = ['twilio', 'iletimerkezi', 'console'] as const;
export type SmsDriver = (typeof SMS_DRIVERS)[number];

export interface SmsSendOptions {
  /** The pack's sender id (`countries.sms_sender_id`): TR `TALKOLOG`, US the toll-free number. */
  sender?: string;
  /** Delivery-status webhook (Twilio). */
  statusCallback?: string;
  /**
   * This send's number on its `messages` row (1 for the first, then one more per retry of the
   * same message on the same channel; W2-15/16 review). Absent outside the Notifier's walk.
   */
  tries?: number;
}

/**
 * One SMS driver. Whether replies reach TalkOlog is the pack's `two_way_sms`, never the driver's:
 * `console` follows the pack too, so the Demo phone takes replies only on two-way packs.
 */
export interface SmsTransport {
  readonly driver: SmsDriver;
  send(to: E164, body: string, opts: SmsSendOptions): Promise<{ providerId: string }>;
}

/**
 * The Notifier's order for a person (§4.5): their preferred channel first when the pack offers it,
 * then the pack's `message_channels`. A preference the pack does not list is ignored (the pack is
 * the allow-list, spec §19 Q21).
 *
 * Push keeps its place in the pack's order whatever the preference (spec §19 Q107: push is never a
 * preference, its own *Notifications* toggle is the choice; W2-14 review): the preference goes
 * first among the other channels, so a reporter who prefers texts and turned notifications on
 * still gets push first on a launch pack, and texts when push fails for good. A stored `push`
 * preference (never offered in Settings) still puts push first.
 *
 * Push is never a person's only channel (spec §9.3; W2-14): it stays only where another channel
 * follows it, so a message that push cannot deliver always has a channel to fall back to (the
 * table refuses a pack whose order ends with push).
 */
export function messageChannelOrder(
  pack: CountryPack,
  preferred: MessageChannel | null,
): MessageChannel[] {
  const order = [...new Set(pack.messageChannels)];
  let ranked: MessageChannel[] = order;
  if (preferred !== null && order.includes(preferred)) {
    if (preferred === 'push') {
      ranked = ['push', ...order.filter((c) => c !== 'push')];
    } else {
      const others = [preferred, ...order.filter((c) => c !== 'push' && c !== preferred)];
      const at = order.indexOf('push');
      ranked = at >= 0 ? [...others.slice(0, at), 'push', ...others.slice(at)] : others;
    }
  }
  const push = ranked.indexOf('push');
  return push >= 0 && push === ranked.length - 1 ? ranked.slice(0, -1) : ranked;
}

/**
 * The channels a Notifier message may go on, in order (§4.5). The order is computed here, so no
 * caller can step outside the pack's allow-list: `messageChannelOrder(pack, preferred)`, kept to
 * the channels in `narrow` when given (a pack that lists no SMS sends nothing by SMS, the device
 * code included, §19 Q374). A channel then stays only if it is built (`implemented`, default
 * `IMPLEMENTED_MESSAGE_CHANNELS`) and carries the key (`CHANNEL_KEYS`); its consent label exists
 * and is held (`PRE_CONSENT_KEYS` skip it on `PRE_CONSENT_CHANNELS` while the person has none,
 * given or revoked; `CONSENT_FREE_KEYS` always, short of a revoked WhatsApp consent); the person
 * has not opted out of it; and, for SMS, the pack allows links when the key carries one (spec §19
 * Q24). Throws on a two-way key: those replies are sms-webhook's (`smsReplyAllowed`).
 */
export function deliverableChannels(input: {
  pack: CountryPack;
  key: NotifierKey;
  /** The person's preferred channel (`reporters.message_channel`); null = the pack's order. */
  preferred: MessageChannel | null;
  /** Keeps only these channels of the order (never adds one). */
  narrow?: readonly MessageChannel[];
  /** The person's unrevoked consent types. */
  consents: ReadonlySet<ConsentType>;
  /** Consent types the person gave and revoked, with no unrevoked consent of that type since. */
  revoked: ReadonlySet<ConsentType>;
  optedOut: ReadonlySet<MessageChannel>;
  implemented?: readonly MessageChannel[];
  /**
   * The consent of a channel the Notifier registered beyond `CONSENT_FOR_CHANNEL` (its
   * `MessageChannelSpec.consent`, W2-01); a channel with no consent label is never deliverable.
   */
  consentFor?: Partial<Record<MessageChannel, ConsentType>>;
}): MessageChannel[] {
  const key: MessageKey = input.key;
  if (isTwoWaySmsKey(key)) {
    throw new Error(`SMS "${key}" answers an inbound text: sms-webhook sends it, not the Notifier`);
  }
  const implemented: readonly MessageChannel[] = input.implemented ?? IMPLEMENTED_MESSAGE_CHANNELS;
  const consentFree = (CONSENT_FREE_KEYS as readonly MessageKey[]).includes(key);
  const base = messageChannelOrder(input.pack, input.preferred);
  const narrow = input.narrow;
  const order = narrow ? base.filter((c) => narrow.includes(c)) : base;
  const neverConsented = input.consents.size === 0 && input.revoked.size === 0;
  const preConsent = neverConsented && (PRE_CONSENT_KEYS as readonly MessageKey[]).includes(key);
  return order.filter((channel) => {
    if (!implemented.includes(channel)) return false;
    if (!channelCarriesKey(channel, input.key)) return false;
    const consent = input.consentFor?.[channel] ?? CONSENT_FOR_CHANNEL[channel];
    if (consent === null) return false;
    if (input.optedOut.has(channel)) return false;
    const firstContact = (PRE_CONSENT_CHANNELS as readonly MessageChannel[]).includes(channel);
    // WhatsApp's STOP is a revoked `whatsapp` consent (§19 Q40): its opt-out.
    const free = consentFree && (channel === 'sms' || !input.revoked.has(consent));
    if (!(firstContact && (preConsent || free)) && !input.consents.has(consent)) return false;
    if (channel === 'sms' && messageKeyHasLink(key) && !input.pack.smsLinksAllowed) return false;
    return true;
  });
}

/**
 * Whether sms-webhook may answer an inbound text with a two-way key (§9.5, F13, F15): HELP →
 * `help`, STOP → `stop_confirm`, CALL / TEXT → `pref_call_ok` / `pref_text_ok` (spec §19 Q46),
 * a sender who is not a reporter → `unknown_number`. These replies
 * never go through the Notifier: they answer a text the person just sent, by SMS, to a sender
 * who may have no reporter row, so neither the person's channel order nor their consent or
 * opt-out applies (HELP is answered before any consent, as the welcome invites it; `stop_confirm`
 * is the one text after STOP). Only the pack decides: it must have two-way SMS, and allow links in
 * SMS for a key that carries one (`help`). The webhook renders with `smsRenderOptions(pack)`,
 * sends through `pickMessageTransport()` with `smsSendOptionsFor(pack)` and audits the reply
 * (`SmsNotifier.reply`). Confirmed by the owner with `PRE_CONSENT_KEYS` (spec §19 Q25,
 * 2026-10-15).
 */
export function smsReplyAllowed(pack: CountryPack, key: ReplySmsKey): boolean {
  if (!pack.twoWaySms) return false;
  if ((WHATSAPP_REPLY_KEYS as readonly string[]).includes(key)) return false;
  return !messageKeyHasLink(key) || pack.smsLinksAllowed;
}

/** The channels a reply to an inbound message goes on: the one the message came in on (W2-16). */
export type ReplyChannel = 'sms' | 'whatsapp';

/**
 * Whether a reply may answer an inbound message on its channel (W2-16; §9.5, §19 Q26): on SMS
 * `smsReplyAllowed` (the pack's two-way SMS and links); on WhatsApp always, on every pack: the
 * person just wrote on WhatsApp, which is two-way and carries links, so the reply goes free-form
 * inside the 24-hour window their message opened.
 */
export function replyAllowed(pack: CountryPack, key: ReplySmsKey, channel: ReplyChannel): boolean {
  return channel === 'whatsapp' ? true : smsReplyAllowed(pack, key);
}

/**
 * The driver of a message channel's transport (§4.5, §5.2), for any registered channel (`specs`,
 * default `BUILT_CHANNEL_SPECS`; the Notifier passes its registry). Per channel: demo orgs always
 * use `console` (they never message real people); so does a pack with no sender of that channel
 * in this environment (nothing goes out before a sender is approved and set,
 * `set_country_endpoints()`); otherwise the pack's provider for the channel. A channel with no
 * registration throws.
 */
export function pickMessageTransport(
  org: { isDemo: boolean },
  pack: CountryPack,
  channel: 'sms',
): SmsDriver;
export function pickMessageTransport(
  org: { isDemo: boolean },
  pack: CountryPack,
  channel: MessageChannel,
  specs?: readonly MessageChannelSpec[],
): string;
export function pickMessageTransport(
  org: { isDemo: boolean },
  pack: CountryPack,
  channel: MessageChannel,
  specs: readonly MessageChannelSpec[] = BUILT_CHANNEL_SPECS,
): string {
  const spec = specs.find((s) => s.channel === channel);
  if (!spec) throw new Error(`Message channel "${channel}" is not built until week 2 (M6)`);
  if (org.isDemo) return 'console';
  if (spec.sender(pack) === null) return 'console';
  return spec.provider(pack);
}

/**
 * The SMS copy variant of a pack: the "or call {number}" clauses, the two-way texts and whether a
 * text may carry a link.
 */
export function smsRenderOptions(pack: CountryPack): RenderSmsOptions {
  return {
    hasVoiceNumber: pack.voiceNumber !== null,
    twoWay: pack.twoWaySms,
    linksAllowed: pack.smsLinksAllowed,
  };
}

/** The send options of a pack's SMS: its sender id, when set in this environment. */
export function smsSendOptionsFor(pack: CountryPack): SmsSendOptions {
  return pack.smsSenderId === null ? {} : { sender: pack.smsSenderId };
}
